/**
 * Chapter 1 Optimized Layout Component
 * 
 * Compact, no-scroll reading experience specifically designed for Chapter 1.
 * Everything fits in one viewport: image, text, and navigation controls.
 * Includes auto-playing videos for story spreads (no video on cue page).
 * Videos play once, restart when returning to a spread, and are muted.
 */

import { motion } from "motion/react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CueInteraction } from "@/components/cue-interaction";
import { BreathingCueChoice } from "@/components/breathing-cue-choice";
import { ForestJourneyMap } from "@/components/forest-journey-map";
import { OakTreeScene } from "@/components/oak-tree-scene";
import { ForestPathScene } from "@/components/forest-path-scene";
import { ScatteredFriendsScene } from "@/components/scattered-friends-scene";
import { PondScene } from "@/components/pond-scene";
import { MilaAndWiseFrogScene } from "@/components/mila-and-wise-frog-scene";
import { BreathingCircleScene } from "@/components/breathing-circle-scene";
import { FriendsTogetherScene } from "@/components/friends-together-scene";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useState, useEffect, useCallback, useRef } from "react";
import { saveReadingPosition } from "@/lib/bookmark";
import { updateCurrentPosition } from "@/lib/platform-tracking";
import { startSession, sendAnalytics, trackButton } from "@/lib/analytics/client";
import { ArrowLeft, ArrowRight, Home, BookOpen, Leaf } from "lucide-react";

// Chapter 1 spreads data with Cloudflare Stream video IDs (7 videos for 7 story spreads)
const chapter1Data = {
  title: "Chapter 1: The Still Pond",
  subtitle: "Mindfulness",
  cue: "Golden Leaf",
  cueIcon: Leaf,
  cueColor: "text-golden-600",
  bgColor: "bg-golden-50",
  spreads: [{
    spread: 0,
    section: "map",
    isMapPage: true, // NEW: Interactive forest map showing journey path
  }, {
    spread: 1,
    section: "story",
    visualComponent: "OakTreeScene",
    videoId: "8a0f04e579fffb10cec86fe1b221efb9",
    text: "Today was the day! Mila the mouse had been wiggling with excitement all week. The adventure was finally here. She and her friends—Pip the porcupine, Bella the bunny, and Grandpa Tortoise—gathered at the big oak tree, clutching their mysterious map."
  }, {
    spread: 2,
    section: "story",
    visualComponent: "ForestPathScene",
    videoId: "ec7cedb7511597789b83932a8a0f027f",
    text: "\"We're going to find every secret spot in the forest!\" Mila squeaked, her tail twitching. Pip bounced on his toes. \"Let's go! Let's go! I want to be the first to find a clue!\" Bella tried to keep up, but her ears flopped in her face as she hurried after the others. Grandpa Tortoise, as always, moved at his own slow and steady pace."
  }, {
    spread: 3,
    section: "story",
    visualComponent: "ScatteredFriendsScene",
    videoId: "464bcbd7a511bb671da8ea45d9aaddd0",
    text: "The friends dashed down the path, leaves crunching under their feet. But soon, Mila realized she couldn't hear Pip's silly songs or Bella's giggles. She turned around—everyone was scattered! Pip was tangled in a bush, Bella looked ready to cry, and Grandpa Tortoise was far behind."
  }, {
    spread: 4,
    section: "story",
    visualComponent: "PondScene",
    videoId: "3b6c049e223a7691c44b12e71eae5150",
    text: "Mila's heart thumped. \"This isn't how I imagined our adventure,\" she thought. \"We're all mixed up and grumpy.\" Just then, they reached a quiet, sparkling pond. On a lily pad sat Wise Frog, eyes closed, breathing in and out, slow and calm."
  }, {
    spread: 5,
    section: "story",
    visualComponent: "MilaAndWiseFrogScene",
    videoId: "44dc2093823fab0ef2d9cc0e5428f933",
    text: "Mila plopped down next to him, her fur all frazzled. \"We're supposed to be having fun, but everything feels wrong.\" Wise Frog opened one eye and smiled. \"Sometimes, the best way to start an adventure is to pause and notice what's around you. Want to try?\""
  }, {
    spread: 6,
    section: "story",
    visualComponent: "BreathingCircleScene",
    videoId: "cd5f0cb1a95b013ded069eda9fde516c",
    text: "Mila wasn't sure, but she nodded. The friends gathered around the pond. Wise Frog showed them how to sit still, close their eyes, and take a slow, deep breath—like smelling a flower. Then they breathed out, slow and gentle, like blowing on a pond to make tiny ripples."
  }, {
    spread: 7,
    section: "cue",
    visualComponent: "BreathingCircleScene",
    // NO VIDEO for cue page - Interactive Golden Leaf activity
    cue: "Golden Leaf",
    cueText: "Take a slow pond breath! Breathe in slowly (smell flower), breathe out gently (pond ripples)."
  }, {
    spread: 8,
    section: "story",
    visualComponent: "FriendsTogetherScene",
    videoId: "4eaa2e988d17b78f85226df43744ac22",
    text: "Mila grinned. \"I feel ready now. Let's start our adventure—together.\" The friends stood up, feeling calm and connected. They had learned their first important lesson: sometimes the best way to begin is to pause, breathe, and notice the world around you."
  }]
};

// Visual component mapping
const visualComponents = {
  OakTreeScene,
  ForestPathScene,
  ScatteredFriendsScene,
  PondScene,
  MilaAndWiseFrogScene,
  BreathingCircleScene,
  FriendsTogetherScene
};

interface Chapter1OptimizedProps {
  onBackToContents: () => void;
}

export function Chapter1Optimized({
  onBackToContents
}: Chapter1OptimizedProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // ✅ FIX: Initialize spread directly from URL parameter
  const spreadParam = searchParams.get('spread');
  const initialSpread = spreadParam ? parseInt(spreadParam) : 0;
  
  // 🐛 DEBUG LOGGING
  console.log('🔍 Chapter 1 initialization:');
  console.log('   URL spread parameter:', spreadParam);
  console.log('   Parsed initialSpread:', initialSpread);
  console.log('   This will display spreads[' + initialSpread + ']');
  console.log('   Spread content:', chapter1Data.spreads[initialSpread]);
  
  const [currentSpread, setCurrentSpread] = useState(initialSpread);
  const [completedCues, setCompletedCues] = useState<Set<string>>(new Set());
  
  // Listen for breathing completion event
  useEffect(() => {
    const handleBreathingComplete = () => {
      setCompletedCues(prev => new Set([...prev, 'breathing-cue']));
      console.log('✅ Breathing cue completed!');
    };
    
    window.addEventListener('breathing-complete', handleBreathingComplete);
    return () => window.removeEventListener('breathing-complete', handleBreathingComplete);
  }, []);
  
  // Iframe ref for tracking (optional - iframes auto-play by design)
  const iframeRef = useRef<HTMLIFrameElement>(null);
  
  // Analytics tracking
  const startTimeRef = useRef<number>(Date.now());
  const sessionIdRef = useRef<string | null>(null);
  const interactionsRef = useRef<string[]>([]);
  const pagesVisitedRef = useRef<Set<number>>(new Set());

  // ✅ FIX: Update spread when URL changes (for map navigation)
  useEffect(() => {
    const spreadParam = searchParams.get('spread');
    const targetSpread = spreadParam ? parseInt(spreadParam) : 0;
    setCurrentSpread(targetSpread);
  }, [searchParams]);

  // Cloudflare Stream iframes auto-play by design (no manual control needed)
  // Just log spread changes for debugging
  useEffect(() => {
    if (currentSpreadData.videoId) {
      console.log(`🎬 Cloudflare Stream video loading - Spread ${currentSpread + 1}, ID: ${currentSpreadData.videoId}`);
    }
  }, [currentSpread]); // Triggers every time spread changes

  // Start analytics session on component mount
  useEffect(() => {
    const initSession = async () => {
      try {
        // Start ONE session for this reading session
        const sessionId = await startSession();
        sessionIdRef.current = sessionId;
        startTimeRef.current = Date.now();
        
        // Track initial page visit in session
        pagesVisitedRef.current.add(currentSpread + 1);
        
        console.log('📊 SINGLE session started for Chapter 1:', sessionId);
      } catch (error) {
        console.warn('Failed to start analytics session:', error);
      }
    };
    
    initSession();
    
    // Basic tracking initialization
    updateCurrentPosition(1, currentSpread + 1);
    
    // ✅ FIX #2: NO CLEANUP - Let TOC handle session ending
    // The chapter component should NOT end the session when unmounting
    // because the user might be navigating to another chapter or Activities
    // Only the TOC page should call endReadingSession() when truly exiting the book
    return () => {
      console.log('📊 Chapter cleanup - Session continues (TOC will handle ending)');
    };
  }, []);

  // Save bookmark and update tracking when spread changes
  useEffect(() => {
    const timeSpent = Math.floor((Date.now() - startTimeRef.current) / 1000);
    
    // Track page visit
    pagesVisitedRef.current.add(currentSpread + 1);
    
    // Save reading position using simple localStorage system
    saveReadingPosition(1, currentSpread + 1);
    
    // 🎯 Send analytics to PostgreSQL using new API
    const sendAnalyticsAsync = async () => {
      try {
        await sendAnalytics(1, currentSpread + 1);
        console.log(`✅ Enhanced analytics sent to PostgreSQL - Chapter 1, Spread ${currentSpread + 1}`);
      } catch (error) {
        console.warn('Failed to send enhanced analytics:', error);
      }
    };
    sendAnalyticsAsync();
    
    console.log(`📖 Chapter 1 - Spread ${currentSpread + 1} viewed (${timeSpent}s, ${interactionsRef.current.length} interactions)`);
    console.log(`📖 User-account bookmark saved: ${chapter1Data.title} - Spread ${currentSpread + 1}`);
  }, [currentSpread]);

  const currentSpreadData = chapter1Data.spreads[currentSpread];
  const isLastSpread = currentSpread === chapter1Data.spreads.length - 1;
  const VisualComponent = currentSpreadData.visualComponent 
    ? visualComponents[currentSpreadData.visualComponent as keyof typeof visualComponents]
    : null;

  // NO AUTO-ADVANCE - User must click glowing icon to start reading

  const handleNext = useCallback(() => {
    // Track navigation interaction
    interactionsRef.current.push(`navigate_next_spread_${currentSpread + 1}`);
    
    if (isLastSpread) {
      interactionsRef.current.push('navigate_to_activities');
      navigate("/book/chapter/1/activity");
    } else {
      setCurrentSpread(prev => prev + 1);
    }
  }, [currentSpread, isLastSpread, navigate]);

  const handlePrevious = useCallback(() => {
    // Track navigation interaction
    interactionsRef.current.push(`navigate_previous_spread_${currentSpread + 1}`);
    
    // Don't allow going back to map page (Spread 0)
    if (currentSpread > 1) {
      setCurrentSpread(prev => prev - 1);
    } else if (currentSpread === 1) {
      // If on Spread 1, go back to TOC instead of map
      navigate("/book/wtbtg");
    }
  }, [currentSpread, navigate]);

  const handleActivities = useCallback(() => {
    // Track navigation interaction
    interactionsRef.current.push('click_activities_button');
    navigate("/book/chapter/1/activity");
  }, [navigate]);

  // Full-screen MAP page (Spread 0 - Forest Journey Map)
  if (currentSpreadData.section === "map") {
    return (
      <div className="fixed inset-0 z-50">
        <ForestJourneyMap
          currentChapter={1}
        />
      </div>
    );
  }

  // Full-screen cue page (no video here) - Chapter 1 uses specialized breathing choice
  if (currentSpreadData.section === "cue") {
    return (
      <div className="fixed inset-0 z-50 bg-gradient-to-br from-yellow-300 via-amber-400 to-orange-500">
        {/* Main Cue Box - Centered */}
        <div className="relative z-10 h-full flex items-center justify-center p-8">
          <motion.div 
            className="bg-cream-50 border-4 border-golden-400 rounded-3xl p-12 text-center mx-auto max-w-2xl"
            style={{ 
              backgroundColor: '#FDF6E3',
              borderColor: '#F59E0B'
            }}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            {/* Celebratory Emojis at Top */}
            <div className="text-5xl mb-8">
              🍃 ✨ 🍃
            </div>
            
            {/* Magic Cue Found Title */}
            <h2 className="text-4xl font-bold mb-10" style={{ color: '#D97706' }}>
              ✨ Magic Cue Found! ✨
            </h2>
            
            {/* Instruction Text */}
            <div className="text-3xl leading-relaxed font-medium mb-6" style={{ color: '#F59E0B' }}>
              Click the glowing Golden Leaf below to try the activity!
            </div>
            
            {/* Chapter 1 ONLY: Specialized breathing choice component with two paths */}
            <div className="absolute top-8 right-8">
              <BreathingCueChoice
                onComplete={() => {
                  setCompletedCues(prev => new Set([...prev, 'breathing-cue']));
                  console.log('✅ Breathing cue completed!');
                }}
                isCompleted={completedCues.has('breathing-cue')}
                trackButton={trackButton}
              />
            </div>
          </motion.div>
        </div>

        {/* Minimal Navigation for Cue Page */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20">
          <div className="flex items-center gap-4 bg-white/90 backdrop-blur-sm rounded-full px-6 py-3 shadow-lg">
            <Button
              onClick={handlePrevious}
              disabled={currentSpread === 0}
              variant="ghost"
              size="sm"
              className="text-forest-600 disabled:opacity-50 hover:bg-transparent hover:text-forest-600"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Prev
            </Button>
            
            <div className="flex items-center gap-2">
              <span className="text-xs text-forest-600">{currentSpread + 1} of {chapter1Data.spreads.length}</span>
            </div>
            
            <Button
              onClick={handleNext}
              variant="ghost"
              size="sm"
              className="text-forest-600 hover:bg-transparent hover:text-forest-600"
            >
              {isLastSpread ? "Activities" : "Next"}
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Normal story page layout with video
  return (
    <div className="h-screen flex flex-col overflow-hidden bg-gradient-to-br from-forest-50 via-golden-50 to-forest-100">
      {/* Compact Navigation Header */}
      <div className="bg-white/90 backdrop-blur-sm border-b border-forest-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-r from-forest-600 to-golden-600 rounded-full flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-sm font-bold text-forest-800">Dira Books</div>
              </div>
            </div>
            <div className="text-forest-400">•</div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onBackToContents}
              className="text-forest-600 hover:bg-transparent hover:text-forest-600"
            >
              <BookOpen className="w-4 h-4 mr-2" />
              Contents
            </Button>
            <div className="text-forest-400">•</div>
            <div className="text-center">
              <h1 className="text-lg font-bold text-forest-800">
                {chapter1Data.title} <span className="text-sm font-normal text-forest-600">• {chapter1Data.subtitle}</span>
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={`${chapter1Data.bgColor} ${chapter1Data.cueColor} border-current text-xs px-2 py-1`}>
              🍃 {chapter1Data.cue}
            </Badge>
            <Button 
              onClick={handleActivities}
              variant="outline" 
              size="sm"
              className="text-xs px-2 py-1 h-7 border-forest-300 text-forest-700 hover:bg-transparent hover:text-forest-600"
            >
              🎯 Activities
            </Button>

          </div>
        </div>
      </div>

      {/* MAIN CONTENT AREA - Split Layout Like Chapter 2 */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* TOP: VIDEO/IMAGE SECTION - 80% height with 170% zoom */}
        <div className="h-[80%] bg-transparent overflow-hidden relative">
          <div className="h-full p-2 flex items-center justify-center">
            {/* PRIORITY: Cloudflare Stream video if available */}
            {currentSpreadData.videoId ? (
              <div className="w-full h-full rounded-lg overflow-hidden bg-gradient-to-br from-forest-50 via-golden-50 to-forest-100 max-w-5xl mx-auto">
                <iframe
                  ref={iframeRef}
                  key={currentSpreadData.videoId}
                  src={`https://iframe.videodelivery.net/${currentSpreadData.videoId}?autoplay=true&muted=true&controls=false&preload=auto&fit=crop`}
                  style={{ 
                    border: 'none', 
                    width: '170%',
                    height: '170%',
                    position: 'relative',
                    left: '50%',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    pointerEvents: 'none'
                  }}
                  allow="autoplay; encrypted-media"
                  title={`Chapter 1 Spread ${currentSpread + 1} Video`}
                  onLoad={() => {
                    console.log(`✅ Cloudflare Stream iframe loaded - Spread ${currentSpread + 1}, ID: ${currentSpreadData.videoId}`);
                  }}
                />
              </div>
            ) : VisualComponent ? (
              <VisualComponent className="w-full h-full object-contain" />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gray-100 rounded-lg">
                <p className="text-gray-500">Loading visual content...</p>
              </div>
            )}
          </div>
        </div>
        
        {/* BOTTOM: TEXT SECTION - 20% height, bigger text, left-aligned */}
        <div className="h-[20%] flex flex-col px-8 py-6">
          <div className="flex-1 flex items-center">
            <div className="text-xl leading-relaxed text-gray-900 whitespace-pre-line max-w-5xl mx-auto" style={{ textAlign: 'left' }}>
              {currentSpreadData.text?.split('\n\n').map((paragraph, index) => (
                <p key={index} className={index > 0 ? 'mt-4' : ''} style={{ textAlign: 'left' }}>
                  {paragraph}
                </p>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Minimal Navigation Controls */}
      <div className="border-t border-gray-200 p-4">
        <div className="flex justify-between items-center py-0 px-2">

          {/* Previous Button - Ultra Minimal */}
          <Button
            onClick={handlePrevious}
            disabled={currentSpread === 0}
            variant="ghost"
            size="sm"
            className="text-forest-600 disabled:opacity-50 px-2 py-0 h-6 hover:bg-transparent hover:text-forest-600"
          >
            <ArrowLeft className="w-3 h-3 mr-1" />
            Prev
          </Button>

          {/* Chapter Progress with Dots - Excludes map page (spread 0) */}
          <div className="flex items-center gap-1">
            <span className="text-xs text-forest-600">Chapter Progress</span>
            <div className="flex gap-1">
              {chapter1Data.spreads.slice(1).map((_, index) => (
                <button
                  key={index + 1}
                  onClick={() => setCurrentSpread(index + 1)}
                  className={`w-2 h-2 rounded-full  ${
                    index + 1 === currentSpread
                      ? 'bg-forest-500'
                      : 'bg-forest-200'
                  }`}
                />
              ))}
            </div>
            <span className="text-xs text-forest-600">{currentSpread} of {chapter1Data.spreads.length - 1}</span>
          </div>

          {/* Next Button - Ultra Minimal */}
          <Button
            onClick={handleNext}
            variant="ghost"
            size="sm"
            className="text-forest-600 px-2 py-0 h-6 hover:bg-transparent hover:text-forest-600"
          >
            {isLastSpread ? "Activities" : "Next"}
            <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}
